import React, { useState, useCallback, useRef } from 'react';

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'info';
  isVisible: boolean;
}

// Estado global do toast
let globalToastState: ToastState = {
  message: '',
  type: 'success',
  isVisible: false,
};

// Lista de listeners para atualizar componentes
const listeners = new Set<() => void>();

// Função para notificar todos os listeners
const notifyListeners = () => {
  listeners.forEach(listener => listener());
};

// Função global para mostrar toast
export const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success', duration: number = 3000) => {
  globalToastState = {
    message,
    type,
    isVisible: true,
  };
  notifyListeners();

  // Auto-hide após a duração especificada (padrão: 3 segundos)
  setTimeout(() => {
    globalToastState = {
      ...globalToastState,
      isVisible: false,
    };
    notifyListeners();
  }, duration);
};

// Hook para usar o toast
export const useToast = () => {
  const [toastState, setToastState] = useState<ToastState>(globalToastState);
  const listenerRef = useRef<() => void>();

  // Função para atualizar o estado local
  const updateState = useCallback(() => {
    setToastState({ ...globalToastState });
  }, []);

  // Registra/desregistra o listener
  React.useEffect(() => {
    listenerRef.current = updateState;
    listeners.add(updateState);

    return () => {
      if (listenerRef.current) {
        listeners.delete(listenerRef.current);
      }
    };
  }, [updateState]);

  const hideToast = useCallback(() => {
    globalToastState = {
      ...globalToastState,
      isVisible: false,
    };
    notifyListeners();
  }, []);

  return {
    ...toastState,
    showToast,
    hideToast,
  };
}; 